/**
 * Free elements that keep their place among the walls when the room's shape
 * changes. Along each axis an element stretches with the walls on either side
 * of it: the space between them scales, and the element scales with it, so a
 * counter running wall to wall still runs wall to wall and a chair keeps its
 * place in the room. A square-on element then lands with its edges on the
 * half-inch grid; a turned one keeps its size and only moves.
 */
import { HALF_INCH, roundToHalfInch } from '../constraints';
import { freeElementRectangleCorners } from '../geometry';
import { resolvePerimeter, type Segment } from '../resolve';
import { type FloorPlanFreeElement, type FloorPlanRoom, freeElementRotation, type Point, roomFreeElements } from '../schemas';

/** The furthest (ft) a wall may have moved and still be taken for the same wall. */
export const FOLLOW_WALL_DISTANCE = 6 / 12;

/** An element this far (ft) past a wall still counts as against it: an inch, for scan noise. */
const PAST_WALL = 1 / 12;

const ALIGNED = 1e-9;

type Axis = 'x' | 'y';
const across = (axis: Axis): Axis => (axis === 'x' ? 'y' : 'x');

/** A wall running across `axis`, by where it stands on `axis` and the span it covers on the other one. */
interface Wall {
  at: number;
  from: number;
  to: number;
}

/** The room's straight walls that stand at a fixed `axis` value. */
function wallsAcross(segments: readonly Segment[], axis: Axis): Wall[] {
  const other = across(axis);
  return segments
    .filter(({ a, b }) => Math.abs(a[axis] - b[axis]) < ALIGNED && Math.abs(a[other] - b[other]) > ALIGNED)
    .map(({ a, b }) => ({ at: a[axis], from: Math.min(a[other], b[other]), to: Math.max(a[other], b[other]) }));
}

interface Extent {
  min: number;
  max: number;
}

const overlaps = (wall: Wall, span: Extent) => wall.from < span.max && wall.to > span.min;

/** Where a wall `was` stands now: the nearest wall in `after`, if one is within FOLLOW_WALL_DISTANCE. */
function wallNow(was: Wall, after: Wall[], span: Extent): number | null {
  const candidates = after.filter(wall => overlaps(wall, span) && Math.abs(wall.at - was.at) <= FOLLOW_WALL_DISTANCE);
  if (!candidates.length) return null;
  return candidates.reduce((best, wall) => (Math.abs(wall.at - was.at) < Math.abs(best.at - was.at) ? wall : best)).at;
}

/** How an element's extent along one axis maps from the old room to the new one. */
interface AxisMap {
  map: (value: number) => number;
  /** The element's high edge stands against a wall and its low edge doesn't, so the high edge is the one to keep in place. */
  anchorHigh: boolean;
}

/**
 * How an element's extent along `axis` maps from `before` to `after`: linear
 * between the nearest walls on either side of it, a plain shift with only one,
 * unchanged with none.
 */
function axisMap(before: Wall[], after: Wall[], extent: Extent, span: Extent): AxisMap {
  const facing = before.filter(wall => overlaps(wall, span));
  const lower = facing.filter(wall => wall.at <= extent.min + PAST_WALL).reduce<Wall | null>((best, wall) => (!best || wall.at > best.at ? wall : best), null);
  const upper = facing.filter(wall => wall.at >= extent.max - PAST_WALL).reduce<Wall | null>((best, wall) => (!best || wall.at < best.at ? wall : best), null);
  const anchorHigh = !!upper && upper.at - extent.max <= PAST_WALL && !(lower && extent.min - lower.at <= PAST_WALL);
  const lowerNow = lower && wallNow(lower, after, span);
  const upperNow = upper && wallNow(upper, after, span);
  if (lower && upper && lowerNow != null && upperNow != null && upper.at > lower.at) {
    const scale = (upperNow - lowerNow) / (upper.at - lower.at);
    return { map: value => lowerNow + (value - lower.at) * scale, anchorHigh };
  }
  if (lower && lowerNow != null) return { map: value => value + lowerNow - lower.at, anchorHigh };
  if (upper && upperNow != null) return { map: value => value + upperNow - upper.at, anchorHigh };
  return { map: value => value, anchorHigh };
}

/** Whether the element sits square to the room: unrotated or turned by whole quarters. */
const isSquare = (element: FloorPlanFreeElement) => Math.abs(Math.round(freeElementRotation(element) / 90) * 90 - freeElementRotation(element)) < ALIGNED;
const isQuarterTurned = (element: FloorPlanFreeElement) => Math.abs(Math.round(freeElementRotation(element) / 90)) % 2 === 1;

/** The walls standing across each axis: before the shape changed, and after. */
type AxisWalls = Record<Axis, [Wall[], Wall[]]>;

const extentOn = (corners: readonly Point[], axis: Axis): Extent => ({
  min: Math.min(...corners.map(p => p[axis])),
  max: Math.max(...corners.map(p => p[axis])),
});

/**
 * A square-on element's low and high edge along one axis once the walls
 * move: the size goes to the grid, then the edge against a wall (the low
 * one, failing that) goes to the grid and the other follows. One too small
 * for the grid just maps.
 */
function edgesOnGrid({ min, max }: Extent, { map, anchorHigh }: AxisMap): [number, number] {
  const size = roundToHalfInch(map(max) - map(min));
  if (size < HALF_INCH) return [map(min), map(max)];
  if (anchorHigh) return [roundToHalfInch(map(max)) - size, roundToHalfInch(map(max))];
  return [roundToHalfInch(map(min)), roundToHalfInch(map(min)) + size];
}

/** A square-on element's frame from its footprint's edges: a quarter-turned element's footprint is its frame turned about its center. */
function frameFromFootprint(element: FloorPlanFreeElement, [left, right]: [number, number], [top, bottom]: [number, number]): FloorPlanFreeElement {
  const [spanX, spanY] = [right - left, bottom - top];
  return isQuarterTurned(element)
    ? { ...element, x: (left + right - spanY) / 2, y: (top + bottom - spanX) / 2, width: spanY, length: spanX }
    : { ...element, x: left, y: top, width: spanX, length: spanY };
}

/** A turned element keeps its size, and its center moves the way the walls around it moved. */
function withCenterMoved(element: FloorPlanFreeElement, maps: Record<Axis, AxisMap>): FloorPlanFreeElement {
  const center = { x: element.x + element.width / 2, y: element.y + element.length / 2 };
  return { ...element, x: maps.x.map(center.x) - element.width / 2, y: maps.y.map(center.y) - element.length / 2 };
}

/** The element moved, and stretched when square-on, the way the walls around it moved; the element itself when its frame comes out the same. */
function followedElement(element: FloorPlanFreeElement, walls: AxisWalls): FloorPlanFreeElement {
  const corners = freeElementRectangleCorners(element);
  const mapOn = (axis: Axis) => axisMap(walls[axis][0], walls[axis][1], extentOn(corners, axis), extentOn(corners, across(axis)));
  const maps = { x: mapOn('x'), y: mapOn('y') };
  const next = isSquare(element)
    ? frameFromFootprint(element, edgesOnGrid(extentOn(corners, 'x'), maps.x), edgesOnGrid(extentOn(corners, 'y'), maps.y))
    : withCenterMoved(element, maps);
  return next.x === element.x && next.y === element.y && next.width === element.width && next.length === element.length ? element : next;
}

/**
 * `after` with each free element stretched and moved the way the walls around
 * it moved from `before` (see the file comment). Returns `after` itself when
 * no element changed.
 */
export function withFreeElementsFollowingWalls(before: FloorPlanRoom, after: FloorPlanRoom): FloorPlanRoom {
  const elements = roomFreeElements(after);
  if (!elements.length) return after;
  const [was, now] = [resolvePerimeter(before).segments, resolvePerimeter(after).segments];
  const walls: AxisWalls = { x: [wallsAcross(was, 'x'), wallsAcross(now, 'x')], y: [wallsAcross(was, 'y'), wallsAcross(now, 'y')] };
  const followed = elements.map(element => followedElement(element, walls));
  return followed.some((element, index) => element !== elements[index]) ? { ...after, freeElements: followed } : after;
}
